import React, { useState, useEffect } from 'react';
import {
  getKingdomVillages,
  saveKingdomVillages,
  resetKingdomVillages,
  UzairueVillage,
  KINGDOM_DATA_CHANGE_EVENT,
} from '../../utils/kingdomDataStorage';

interface AdminVillagesManagerProps {
  adminEmail?: string;
  adminName?: string;
}

export const AdminVillagesManager: React.FC<AdminVillagesManagerProps> = ({
  adminEmail = 'erueuzairue@gmail.com',
  adminName = 'Ikpemhi Oshioke',
}) => {
  const [villages, setVillages] = useState<UzairueVillage[]>(() => getKingdomVillages());
  const [toastMessage, setToastMessage] = useState<string | null>(null);

  // Edit or Add Modal state
  const [editingVillage, setEditingVillage] = useState<UzairueVillage | null>(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  // Form fields
  const [formName, setFormName] = useState('');
  const [formLeader, setFormLeader] = useState('');
  const [formPop, setFormPop] = useState('');
  const [formSig, setFormSig] = useState('');
  const [formTroupe, setFormTroupe] = useState('');

  useEffect(() => {
    const handleUpdate = (e: any) => {
      if (e.detail?.villages) {
        setVillages(e.detail.villages);
      } else {
        setVillages(getKingdomVillages());
      }
    };
    window.addEventListener(KINGDOM_DATA_CHANGE_EVENT, handleUpdate);
    return () => window.removeEventListener(KINGDOM_DATA_CHANGE_EVENT, handleUpdate);
  }, []);

  const showToast = (msg: string) => {
    setToastMessage(msg);
    setTimeout(() => setToastMessage(null), 3500);
  };

  const handleOpenAdd = () => {
    setEditingVillage(null);
    setFormName('');
    setFormLeader('');
    setFormPop('7,500');
    setFormSig('');
    setFormTroupe('');
    setIsModalOpen(true);
  };

  const handleOpenEdit = (v: UzairueVillage) => {
    setEditingVillage(v);
    setFormName(v.name);
    setFormLeader(v.dauduOrLeader);
    setFormPop(v.populationEstimate);
    setFormSig(v.culturalSignificance);
    setFormTroupe(v.activeTroupe);
    setIsModalOpen(true);
  };

  const handleSaveVillage = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formName.trim()) {
      alert('Village name is required.');
      return;
    }

    let updated: UzairueVillage[];
    if (editingVillage) {
      updated = villages.map((item) =>
        item.id === editingVillage.id
          ? {
              ...item,
              name: formName.trim(),
              dauduOrLeader: formLeader.trim() || 'Community Leader',
              populationEstimate: formPop.trim() || '7,000',
              culturalSignificance: formSig.trim() || 'Component community of Uzairue Kingdom',
              activeTroupe: formTroupe.trim() || 'Cultural Troupe',
            }
          : item
      );
      showToast(`✨ Updated village "${formName.trim()}" successfully!`);
    } else {
      const newV: UzairueVillage = {
        id: `v-${Date.now()}`,
        name: formName.trim(),
        dauduOrLeader: formLeader.trim() || 'Community Leader',
        populationEstimate: formPop.trim() || '7,000',
        culturalSignificance: formSig.trim() || 'Component community of Uzairue Kingdom',
        activeTroupe: formTroupe.trim() || 'Cultural Troupe',
      };
      updated = [newV, ...villages];
      showToast(`✨ Added new village "${formName.trim()}" to Uzairue 22 Communities!`);
    }

    saveKingdomVillages(updated, `${adminName} (${adminEmail})`);
    setIsModalOpen(false);
  };

  const handleDeleteVillage = (id: string, name: string) => {
    if (confirm(`Are you sure you want to remove ${name}?`)) {
      const updated = villages.filter((v) => v.id !== id);
      saveKingdomVillages(updated, `${adminName} (${adminEmail})`);
      showToast(`🗑️ Removed ${name}. Total communities: ${updated.length}`);
    }
  };

  const handleResetVillages = () => {
    if (confirm('Reset all 22 Uzairue villages and component communities to royal defaults?')) {
      const defs = resetKingdomVillages();
      setVillages(defs);
      showToast('🔄 Restored default 22 Uzairue villages.');
    }
  };

  return (
    <div className="space-y-6 text-gray-800">
      {/* Header */}
      <div className="bg-gradient-to-r from-emerald-950 via-slate-900 to-emerald-950 text-white p-5 rounded-2xl border border-amber-400/40 shadow-xl flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <span className="text-2xl">🏛️</span>
            <div>
              <h3 className="text-lg font-serif font-black text-amber-300 uppercase tracking-wide">
                Uzairue 22 Villages & Component Communities Manager
              </h3>
              <p className="text-xs text-emerald-200">
                Corrected across the entire project (22 Communities) • Fully Editable & App-Wide Sync
              </p>
            </div>
          </div>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={handleOpenAdd}
            className="px-4 py-2 bg-amber-500 hover:bg-amber-400 text-slate-950 font-black text-xs uppercase tracking-wider rounded-xl transition-all cursor-pointer shadow-md"
          >
            + Add Community
          </button>
          <button
            onClick={handleResetVillages}
            className="px-3 py-2 bg-red-900/60 hover:bg-red-800 text-red-200 border border-red-500/50 rounded-xl text-xs font-bold transition-all cursor-pointer"
          >
            Reset 22 Villages
          </button>
        </div>
      </div>

      {/* Toast */}
      {toastMessage && (
        <div className="bg-emerald-950 border-2 border-amber-400 text-amber-300 p-3 rounded-xl text-xs font-black flex items-center justify-between shadow-xl animate-fade-in">
          <span>{toastMessage}</span>
          <span className="text-emerald-400 font-mono text-[10px]">SYNCED PROJECT-WIDE</span>
        </div>
      )}

      {/* Info Banner */}
      <div className="bg-amber-50 border border-amber-300 p-4 rounded-2xl text-xs text-amber-900 flex items-center justify-between">
        <div>
          <strong className="font-bold block text-sm">Uzairue Kingdom Structure</strong>
          <span className="text-amber-800">
            Uzairue consists of precisely <strong className="text-emerald-950 underline">22 villages and component communities</strong>. All mentions of clans or counts across navigation, directories, broadcasts, and heritage lists have been updated.
          </span>
        </div>
        <div className="bg-emerald-900 text-amber-300 px-3 py-1.5 rounded-xl text-xs font-black shrink-0 border border-amber-400/30">
          Total: {villages.length} Communities
        </div>
      </div>

      {/* Villages Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {villages.map((v, idx) => (
          <div key={v.id} className="bg-white p-4 rounded-2xl border border-gray-200 shadow-xs space-y-3 flex flex-col justify-between hover:border-amber-400 transition-all">
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-[10px] font-mono bg-emerald-100 text-emerald-900 font-bold px-2 py-0.5 rounded">
                  Village #{idx + 1}
                </span>
                <span className="text-[10px] text-gray-500 font-mono">Pop: {v.populationEstimate}</span>
              </div>

              <h4 className="text-sm font-serif font-black text-emerald-950">{v.name}</h4>
              <p className="text-xs text-gray-700 font-medium">👑 {v.dauduOrLeader}</p>
              <p className="text-[11px] text-gray-600 line-clamp-2">{v.culturalSignificance}</p>
              <p className="text-[10px] text-emerald-700 font-mono bg-emerald-50 px-2 py-1 rounded">
                🎭 {v.activeTroupe}
              </p>
            </div>

            <div className="flex items-center justify-between pt-2 border-t border-gray-100 text-xs">
              <button
                onClick={() => handleOpenEdit(v)}
                className="text-amber-700 hover:text-amber-900 font-bold cursor-pointer"
              >
                Edit Details
              </button>
              <button
                onClick={() => handleDeleteVillage(v.id, v.name)}
                className="text-red-600 hover:text-red-800 font-bold cursor-pointer"
              >
                Delete
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* Edit / Add Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-amber-400">
            <div className="flex items-center justify-between border-b pb-3">
              <h4 className="text-base font-serif font-black text-emerald-950">
                {editingVillage ? 'Edit Village / Community' : 'Add New Village / Community'}
              </h4>
              <button
                onClick={() => setIsModalOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-sm font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <form onSubmit={handleSaveVillage} className="space-y-3 text-xs">
              <div>
                <label className="block font-bold text-gray-700 mb-1">Village Name (e.g. Jattu-Uzairue):</label>
                <input
                  type="text"
                  value={formName}
                  onChange={(e) => setFormName(e.target.value)}
                  placeholder="Village Name"
                  className="w-full border border-gray-300 rounded-lg p-2 font-bold"
                  required
                />
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Daudu or Community Leader Name:</label>
                <input
                  type="text"
                  value={formLeader}
                  onChange={(e) => setFormLeader(e.target.value)}
                  placeholder="e.g. High Chief Aliu Momoh"
                  className="w-full border border-gray-300 rounded-lg p-2"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Population Estimate:</label>
                  <input
                    type="text"
                    value={formPop}
                    onChange={(e) => setFormPop(e.target.value)}
                    placeholder="e.g. 15,000"
                    className="w-full border border-gray-300 rounded-lg p-2"
                  />
                </div>
                <div>
                  <label className="block font-bold text-gray-700 mb-1">Active Cultural Troupe:</label>
                  <input
                    type="text"
                    value={formTroupe}
                    onChange={(e) => setFormTroupe(e.target.value)}
                    placeholder="e.g. Royal Trumpeters"
                    className="w-full border border-gray-300 rounded-lg p-2"
                  />
                </div>
              </div>

              <div>
                <label className="block font-bold text-gray-700 mb-1">Cultural Significance & History:</label>
                <textarea
                  value={formSig}
                  onChange={(e) => setFormSig(e.target.value)}
                  placeholder="Describe the cultural significance, historical milestones, or trade heritage..."
                  className="w-full border border-gray-300 rounded-lg p-2 h-20"
                />
              </div>

              <div className="flex justify-end gap-2 pt-3 border-t">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 bg-gray-200 hover:bg-gray-300 text-gray-800 rounded-xl font-bold cursor-pointer"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-5 py-2 bg-emerald-800 hover:bg-emerald-900 text-amber-300 rounded-xl font-black cursor-pointer shadow"
                >
                  Save Community
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminVillagesManager;
